import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { ServiceCategory } from '@planner/types'

export function useServiceCategories() {
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCategories = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('service_categories')
        .select('*')
        .order('display_order', { ascending: true })

      if (fetchError) {
        setError(fetchError.message)
      } else {
        setCategories(data || [])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const createCategory = useCallback(async (category: Omit<ServiceCategory, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error: createError } = await supabase
        .from('service_categories')
        .insert(category)
        .select()
        .single()

      if (createError) {
        return { error: createError.message, data: null }
      }

      await fetchCategories()
      return { error: null, data }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd', data: null }
    }
  }, [fetchCategories])

  const updateCategory = useCallback(async (id: string, updates: Partial<ServiceCategory>) => {
    try {
      const { data, error: updateError } = await supabase
        .from('service_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (updateError) {
        return { error: updateError.message, data: null }
      }

      await fetchCategories()
      return { error: null, data }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd', data: null }
    }
  }, [fetchCategories])

  const deleteCategory = useCallback(async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('service_categories')
        .delete()
        .eq('id', id)

      if (deleteError) {
        return { error: deleteError.message }
      }

      await fetchCategories()
      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [fetchCategories])

  return {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  }
}


